/**
 * Esprima Studios — Stone Options Module
 * -------------------------------------------
 * Renders a "Request a different stone" form scoped to whichever variant is
 * currently selected on a product page, using the rules in
 * stone-config.js (window.EsprimaStoneConfig, keyed by variant SKU).
 *
 * On Add to Cart, the buyer's request is attached to the product form as
 * standard Shopify line-item properties (properties[...]) so it lands on
 * the order the same way an engraving request does — no app or
 * integration required, staff relay it to Stuller manually.
 *
 * Depends on: stone-config.js (must load first), stone-options.css
 */
(function () {
  "use strict";

  var ROOT_ID = "stone-options";

  function init() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;

    var config = window.EsprimaStoneConfig || {};
    var variantMapEl = document.querySelector("[data-stone-variant-map]");
    var variantMap = {};
    if (variantMapEl) {
      try {
        var parsed = JSON.parse(variantMapEl.textContent);
        (parsed.variants || []).forEach(function (v) {
          variantMap[String(v.id)] = v.sku;
        });
      } catch (e) {
        console.warn("[stone-options] could not parse variant map", e);
      }
    }

    var form = root.closest("form") || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Some themes assemble the add-to-cart form from a block system where our
    // module's container isn't a DOM descendant of the real <form> (e.g.
    // Shopify's newer block-based product templates). The HTML `form`
    // attribute associates a control with a form by id regardless of DOM
    // placement, and FormData(form) picks those up automatically — so every
    // dynamically created field that should actually submit gets tagged with
    // this once we know the form's id. Harmless no-op on themes where our
    // container IS nested inside the form already.
    //
    // IMPORTANT: use getAttribute("id") here, not the `.id` property. Shopify's
    // cart form contains `<input name="id" value="{variant.id}">` (the variant
    // ID field), and per the HTML spec a form-associated element named "id"
    // shadows the form's own `.id` IDL property — so `form.id` resolves to that
    // input element, not the form's id string. `getAttribute("id")` reads the
    // actual id="" attribute and isn't affected by this shadowing.
    var formId = form.getAttribute("id");
    if (!formId) {
      formId = "esprima-options-form-" + Math.random().toString(36).slice(2);
      form.setAttribute("id", formId);
    }
    function bindToForm(el) {
      el.setAttribute("form", formId);
      return el;
    }

    var variantInput = form.querySelector('[name="id"]');
    var state = { spec: null, sku: null, locations: {} };

    function currentVariantId() {
      if (!variantInput) return null;
      return variantInput.value || variantInput.getAttribute("value");
    }

    function resolveSpec() {
      var id = currentVariantId();
      var sku = id != null ? variantMap[String(id)] : null;
      state.sku = sku || null;
      state.spec = sku ? config[sku] : null;
      return state.spec;
    }

    function render() {
      var spec = resolveSpec();
      root.innerHTML = "";

      if (!spec) {
        root.hidden = true;
        return;
      }
      root.hidden = false;
      root.appendChild(buildModule(spec));
    }

    function buildModule(spec) {
      var wrap = document.createElement("div");
      wrap.className = "stone-module";

      var toggleId = "stone-toggle";
      var toggleWrap = document.createElement("label");
      toggleWrap.className = "stone-toggle";
      toggleWrap.innerHTML =
        '<input type="checkbox" id="' + toggleId + '"> ' +
        "<span>Request a different stone for this piece</span>";
      wrap.appendChild(toggleWrap);

      var body = document.createElement("div");
      body.className = "stone-body";
      body.hidden = true;
      wrap.appendChild(body);

      var intro = document.createElement("p");
      intro.className = "stone-intro";
      intro.textContent =
        "This piece ships with " + spec.locations[0].defaultOrigin.toLowerCase() +
        " by default. " +
        (spec.locations.length > 1
          ? "Choose the stone origin for each setting below."
          : "Choose the stone origin below.") +
        " We'll confirm exact pricing and availability with you before the order is placed.";
      body.appendChild(intro);

      var locationsEl = document.createElement("div");
      locationsEl.className = "stone-locations";
      body.appendChild(locationsEl);

      spec.locations.forEach(function (loc) {
        locationsEl.appendChild(buildLocationField(spec, loc));
      });

      var notesField = document.createElement("div");
      notesField.className = "stone-field stone-notes-field";
      var notesLabel = document.createElement("label");
      notesLabel.setAttribute("for", "stone-notes");
      notesLabel.textContent = "Anything else we should know about this request? (optional)";
      notesField.appendChild(notesLabel);
      var notesInput = document.createElement("textarea");
      notesInput.id = "stone-notes";
      notesInput.name = "properties[Stone Request — Notes]";
      notesInput.rows = 2;
      notesInput.disabled = true;
      bindToForm(notesInput);
      notesField.appendChild(notesInput);
      body.appendChild(notesField);
      state.notesInput = notesInput;

      var summary = document.createElement("div");
      summary.className = "stone-summary";
      summary.setAttribute("aria-live", "polite");
      body.appendChild(summary);

      var noteEl = document.createElement("p");
      noteEl.className = "stone-fine-print";
      noteEl.textContent =
        "Requesting a stone change may affect price and lead time. We'll reach out to " +
        "confirm both before your order is sent to production.";
      body.appendChild(noteEl);

      function refreshSummary() {
        var parts = [];
        spec.locations.forEach(function (loc) {
          var s = state.locations[loc.id];
          if (!s || !s.enabled) return;
          var chosen = loc.origins.filter(function (o) { return o.value === s.origin; })[0];
          if (chosen && s.origin !== defaultOriginValue(loc)) {
            parts.push(loc.label + ": " + chosen.label.replace(" (current default)", ""));
          }
        });
        summary.textContent = parts.length
          ? "Your request — " + parts.join("  |  ")
          : (toggleWrap.querySelector("input").checked
              ? "No change selected yet — pick a stone origin above, or turn this off to keep the current default."
              : "");
      }

      toggleWrap.querySelector("input").addEventListener("change", function (e) {
        body.hidden = !e.target.checked;
        Object.keys(state.locations).forEach(function (id) {
          var s = state.locations[id];
          s.enabled = e.target.checked;
          Object.keys(s.inputs).forEach(function (key) {
            var el = s.inputs[key];
            if (Array.isArray(el)) {
              el.forEach(function (radio) { radio.disabled = !e.target.checked; });
            } else if (el) {
              el.disabled = !e.target.checked;
            }
          });
        });
        notesInput.disabled = !e.target.checked;
        refreshSummary();
      });

      wrap.__refreshSummary = refreshSummary;
      wrap.__body = body;

      return wrap;
    }

    function defaultOriginValue(loc) {
      var match = loc.origins.filter(function (o) {
        return loc.defaultOrigin && o.label.indexOf(loc.defaultOrigin) === 0;
      })[0];
      return match ? match.value : loc.origins[0].value;
    }

    function buildLocationField(spec, loc) {
      var fieldset = document.createElement("fieldset");
      fieldset.className = "stone-location";

      var legend = document.createElement("legend");
      legend.textContent = loc.label;
      fieldset.appendChild(legend);

      var details = document.createElement("p");
      details.className = "stone-details";
      var detailParts = [loc.shape, loc.size];
      if (loc.carat) detailParts.push(loc.carat);
      details.textContent = detailParts.filter(Boolean).join(" · ");
      fieldset.appendChild(details);

      var defaultValue = defaultOriginValue(loc);
      var s = {
        enabled: false,
        origin: defaultValue,
        inputs: {}
      };
      state.locations[loc.id] = s;

      var namePrefix = "Stone — " + loc.label + " — ";
      var useDropdown = loc.inputType === "select" || loc.origins.length > 4;

      var priceNote = document.createElement("p");
      priceNote.className = "stone-price-note";
      priceNote.hidden = true;

      // Hidden field carrying the actual request value for cart submission —
      // only meaningful (and only enabled) once the toggle is on.
      var originField = document.createElement("input");
      originField.type = "hidden";
      originField.name = "properties[" + namePrefix + "Requested Stone]";
      originField.disabled = true;
      bindToForm(originField);

      var defaultField = document.createElement("input");
      defaultField.type = "hidden";
      defaultField.name = "properties[" + namePrefix + "Current Default]";
      defaultField.value = loc.defaultOrigin + " (" + [loc.shape, loc.size, loc.carat].filter(Boolean).join(", ") + ")";
      defaultField.disabled = true;
      bindToForm(defaultField);
      s.inputs.defaultField = defaultField;

      function setOrigin(value) {
        s.origin = value;
        var chosen = loc.origins.filter(function (o) { return o.value === value; })[0];
        originField.value = chosen ? chosen.label.replace(" (current default)", "") : value;
        if (chosen && chosen.priceNote) {
          priceNote.textContent = chosen.priceNote;
          priceNote.hidden = false;
        } else {
          priceNote.hidden = true;
        }
      }

      if (useDropdown) {
        var selectId = "stone-origin-" + loc.id;
        var selectLabel = document.createElement("label");
        selectLabel.setAttribute("for", selectId);
        selectLabel.className = "stone-select-label";
        selectLabel.textContent = "Requested stone";
        fieldset.appendChild(selectLabel);

        var select = document.createElement("select");
        select.id = selectId;
        select.name = "stone-origin-select-" + loc.id;
        select.disabled = true;
        loc.origins.forEach(function (o) {
          var opt = document.createElement("option");
          opt.value = o.value;
          opt.textContent = o.label + (o.grade ? " — " + o.grade : "");
          if (o.value === defaultValue) opt.selected = true;
          select.appendChild(opt);
        });
        fieldset.appendChild(select);
        s.inputs.originSelect = select;

        select.addEventListener("change", function (e) {
          setOrigin(e.target.value);
          fieldset.dispatchEvent(new CustomEvent("stone:local-update", { bubbles: true }));
        });
      } else {
        var originRow = document.createElement("div");
        originRow.className = "stone-origin-row";
        loc.origins.forEach(function (o) {
          var id = "stone-origin-" + loc.id + "-" + o.value;
          var label = document.createElement("label");
          label.className = "stone-radio";
          var isDefault = o.value === defaultValue;
          label.innerHTML =
            '<input type="radio" name="stone-origin-' + loc.id + '" id="' + id + '" value="' + o.value + '"' +
            (isDefault ? " checked" : "") + " disabled> " +
            "<span>" + o.label + "</span>" +
            (o.grade ? '<span class="stone-grade">' + o.grade + "</span>" : "");
          originRow.appendChild(label);
        });
        fieldset.appendChild(originRow);
        s.inputs.originRadios = Array.prototype.slice.call(originRow.querySelectorAll('input[type="radio"]'));

        originRow.addEventListener("change", function (e) {
          if (e.target.name !== "stone-origin-" + loc.id) return;
          setOrigin(e.target.value);
          fieldset.dispatchEvent(new CustomEvent("stone:local-update", { bubbles: true }));
        });
      }

      fieldset.appendChild(priceNote);
      fieldset.appendChild(originField);
      fieldset.appendChild(defaultField);
      s.inputs.originField = originField;

      setOrigin(defaultValue);

      fieldset.addEventListener("stone:local-update", function () {
        var moduleEl = fieldset.closest(".stone-module");
        if (moduleEl && moduleEl.__refreshSummary) moduleEl.__refreshSummary();
      });

      return fieldset;
    }

    if (variantInput) {
      variantInput.addEventListener("change", render);
    }
    form.addEventListener("variant:change", render);
    form.addEventListener("on:variant:change", render);

    render();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
